import {MODSWorkflowManagementClient} from "../../client/mods-workflow-management-client";
import {GremlinQueryResultClass} from "../../util/umlc-gremlin-query-utils";

// Interface defining the structure of a model test result
interface ModelTestResult {
    experimentId: string;
    namespace: string;
    region: string;
    createdAt: Date;
    modelName: string;
    modelType: string;
    dataset: string;
    modelAuthor: string;
    manualUserAcceptance: boolean;
    [key: string]: string | number | Date | boolean; // This allows for dynamic metric fields
}

// Generates a Gremlin query to fetch the current user's models
export const getMyModelsQuery = (currentUser: string) => `
  g.V().hasLabel("Test")
    .has("namespace", "AMLET")
    .has("owner", "${currentUser}")
    .project("experimentId", "namespace", "region", "result", "createdAt", "modelAuthor", "modelName", "modelType", "datasetName","document")
    .by("$uri")
    .by("namespace")
    .by("region")
    .by("result")
    .by("createdAt")
    .by("owner")
    .by(coalesce(outE("tested").inV().values("$uri"), constant("No model name available")))
    .by(coalesce(outE("whenUsedWith").inV().values("modelType"), constant("No model type available")))
    .by(coalesce(outE("whenUsedWith").inV().values("$uri"), constant("No dataset name available")))
    .by("$document")
`;

// Extracts and formats search results from the Gremlin query response
function extractSearchResults(result: any, keys:any) {
    const gremlinResult = GremlinQueryResultClass.fromJSON(result.data.queryResult);
    const rawResults = gremlinResult.readValues(keys);

    return rawResults.map(item => {
        let resultString: string;
        resultString = item.result;
        const resultObject: Record<string, number | string> = {};

        // Parse the result string into key-value pairs
        resultString.slice(1, -1).split(',').forEach(pair => {
            // Get the key and parse the rest as value.
            let [key, ...valueParts] = pair.split(':');
            let value = valueParts.join(':');
            key = key.trim();
            // Remove quotation marks from the key if present
            if (key.startsWith('"') && key.endsWith('"')) {
                key = key.slice(1, -1);
            }

            value = value.trim();

            if (value.includes('=')) {
                const [label, numStr] = value.split('=');
                const num = parseFloat(numStr);
                resultObject[key] = !isNaN(num) ?`${label}=${num.toFixed(4)}`: value;
            }
            else {
                const num = parseFloat(value);
                console.info("Displaying results without formatting as the " +
                    "values don't follow the required format: label=value");
                resultObject[key] = !isNaN(num) ? Number(num.toFixed(4)) : value;
            }
        });

        // Parse the document to extract manualUserAcceptance
        let manualUserAcceptance = false;
        if (item.document) {
            const lowercaseDocument = item.document.toLowerCase();
            const regex = /user-acceptance\\":\\"(true|false)\\"/;
            const match = lowercaseDocument.match(regex);

            if (match) {
                manualUserAcceptance = match[1] === 'true';
                console.log("Manual User Acceptance: ")
                console.log(manualUserAcceptance, match[0], match[1]);
            }
        }

        const baseResult: ModelTestResult = {
            experimentId: item.experimentId,
            namespace: item.namespace,
            region: item.region,
            createdAt: new Date(item.createdAt),
            modelName: item.modelName,
            modelType: item.modelType,
            dataset: item.datasetName,
            modelAuthor: item.modelAuthor,
            manualUserAcceptance: manualUserAcceptance
        };
        return {...baseResult, ...resultObject};
    });
}

// Fetches and processes the results of the user's models
export const GetMyModelsResult = (modsWorkflowManagementClient: MODSWorkflowManagementClient, currentUser: string): Promise<ModelTestResult[]> => {
    let personalizedQuery = cleanQuery(getMyModelsQuery(currentUser));
    return modsWorkflowManagementClient
        .executeGremlinQueryCognito(personalizedQuery)
        .then((result) => {
            if(!result){
                throw new Error('Failed to get a response');
            }
            if (result.status !== 200) {
                throw new Error('Failed to fetch data');
            }

            return extractSearchResults(result, ['experimentId', 'namespace', 'region', 'result',
                'createdAt', 'modelAuthor','modelName', 'modelType', 'datasetName',"document"]);
        })
        .catch((error) => {
            console.error('Error fetching data:', error);
            throw error;
        });
}

// Generates a Gremlin query for searching models based on provided parameters
export const getSearchModelsQuery = (params: {
    modelType?: string;
    dataset?: string;
    modelName?: string;
    modelAuthor?: string;
    startDate?: string;
    endDate?: string;
}) => {
    let query = `
        g.V().hasLabel("Test")
        .has("namespace", "AMLET")
    `;

    if (params.modelType && params.modelType !== "All_Types") {
        query += `.where(outE("whenUsedWith").inV().has("modelType", "${params.modelType}"))`;
    }

    if (params.dataset) {
        query += `.where(outE("whenUsedWith").inV().has("$uri", "${params.dataset}"))`;
    }

    if (params.modelName) {
        query += `.where(outE("tested").inV().has("$uri", "${params.modelName}"))`;
    }

    if (params.modelAuthor) {
        query += `.has("owner", "${params.modelAuthor}")`;
    }

    if (params.startDate && params.endDate) {
        query += `.has("createdAt", between(datetime("${params.startDate}"), datetime("${params.endDate}")))`;
    }

    query += `
        .project("experimentId", "namespace", "region", "result", "createdAt", "modelAuthor", "modelName", "modelType", "datasetName")
        .by("$uri")
        .by("namespace")
        .by("region")
        .by("result")
        .by("createdAt")
        .by("owner")
        .by(coalesce(outE("tested").inV().values("$uri"), constant("No model name available")))
        .by(coalesce(outE("whenUsedWith").inV().values("modelType"), constant("No model type available")))
        .by(coalesce(outE("whenUsedWith").inV().values("$uri"), constant("No dataset name available")))
    `;

    return query;
};

// Executes a search for models based on the provided parameters
export const SearchModelsResult = (
    modsWorkflowManagementClient: MODSWorkflowManagementClient,
    params: {
        modelType?: string;
        dataset?: string;
        modelName?: string;
        modelAuthor?: string;
        startDate?: string;
        endDate?: string;
    }
): Promise<ModelTestResult[]> => {
    let searchQuery = cleanQuery(getSearchModelsQuery(params));

    return modsWorkflowManagementClient
        .executeGremlinQueryCognito(searchQuery)
        .then((result) => {
            if(!result){
                throw new Error('Failed to get a response');
            }
            if (result.status !== 200) {
                throw new Error('Failed to fetch data');
            }
            return extractSearchResults(result, ["experimentId",
                "namespace", "region", "result", "createdAt", "modelAuthor", "modelName", "modelType", "datasetName"]);
        })
        .catch((error) => {
            console.error('Error fetching data:', error);
            throw error;
        });
};

// Converts a GremlinQueryResult to an array of strings
function readValuesAsStringArray(gremlinResult: any) {
    if (Array.isArray(gremlinResult.value) && gremlinResult.value.every(item => typeof item === 'string')) {
        return gremlinResult.value;
    }
    throw new Error('Unexpected result format');
}

// Fetches and returns a list of model names
export const GetModelNames = async (modsWorkflowManagementClient: MODSWorkflowManagementClient): Promise<string[]> => {
    let query =   `g.V().hasLabel('Model')
        .has('namespace', 'AMLET')
        .values('$uri')
        `;
    query = cleanQuery(query);

    try {
        console.log(`Executing Gremlin query: ${query}`);
        let result = await modsWorkflowManagementClient
            .executeGremlinQueryCognito(query);
        if(!result){
            console.error('Result is undefined');
            throw new Error('Failed to get a response');
        }
        if (result.status !== 200) {
            console.error('Non-200 status code:', result);
            throw new Error('Failed to fetch data');
        }
        const gremlinResult = GremlinQueryResultClass.fromJSON(result.data.queryResult);
        return readValuesAsStringArray(gremlinResult);
    } catch (error) {
        console.error('Error fetching model names:', error);
        throw error;
    }
}

// Fetches and returns a list of dataset names
export const GetDatasetNames = (modsWorkflowManagementClient: MODSWorkflowManagementClient): Promise<string[]> => {
    let query = `
      g.V().hasLabel('Stream')
        .has('namespace', 'AMLET')
        .has('$type', 'ModelingData')
        .values('$uri')
      `;
    query = cleanQuery(query);

    return modsWorkflowManagementClient
        .executeGremlinQueryCognito(query)
        .then((result) => {
            if(!result){
                throw new Error('Failed to get a response');
            }
            if (result.status !== 200) {
                throw new Error('Failed to fetch data');
            }
            const gremlinResult = GremlinQueryResultClass.fromJSON(result.data.queryResult);
            return readValuesAsStringArray(gremlinResult);
        })
        .catch((error) => {
            console.error('Error fetching dataset names:', error);
            throw error;
        });
}

// Function to remove all newline characters and escape all double quotes in the query
function cleanQuery(query: string) {
    return query.replace(/\n/g, '');
}